import { useMemo, useState } from 'react';
import { CalendarX2, ChevronLeft, ChevronRight } from 'lucide-react';
import { EmptyState } from '../components/dashboard/EmptyState';
import { buildCalendarDays, formatTime, toDateKey } from './calendar-utils';

export type CalendarItem = {
  id: string;
  date: string;
  kind: 'task' | 'event';
  title: string;
  time?: string | null;
};

type Props = {
  items?: readonly CalendarItem[];
  timeFormat?: 12 | 24;
  weekStartsOn?: 0 | 1;
};

export const CalendarView = ({ items = [], timeFormat = 12, weekStartsOn = 0 }: Props) => {
  const today = useMemo(() => new Date(), []);
  const [visibleMonth, setVisibleMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(() => toDateKey(today));
  const days = useMemo(() => buildCalendarDays(visibleMonth, weekStartsOn), [visibleMonth, weekStartsOn]);
  const selectedItems = items.filter((item) => item.date === selectedDate);
  const monthLabel = new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' }).format(visibleMonth);
  const longDateFormat = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' } as const;
  const selectedLabel = new Intl.DateTimeFormat('en', longDateFormat).format(new Date(`${selectedDate}T12:00:00`));

  const moveMonth = (offset: number) => {
    setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + offset, 1));
  };

  const returnToToday = () => {
    setVisibleMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDate(toDateKey(today));
  };

  return (
    <div className="calendar-layout">
      <section className="calendar-card" aria-labelledby="calendar-month">
        <div className="calendar-toolbar">
          <div>
            <p className="eyebrow">Monthly view</p>
            <h2 id="calendar-month" aria-live="polite">{monthLabel}</h2>
          </div>
          <div className="calendar-controls">
            <button type="button" className="secondary-button today-button" onClick={returnToToday}>Today</button>
            <button type="button" className="icon-button" aria-label="Previous month" onClick={() => moveMonth(-1)}><ChevronLeft size={20} /></button>
            <button type="button" className="icon-button" aria-label="Next month" onClick={() => moveMonth(1)}><ChevronRight size={20} /></button>
          </div>
        </div>
        <div className="calendar-weekdays" aria-hidden="true">
          {(weekStartsOn === 1 ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']).map((day) => <span key={day}>{day}</span>)}
        </div>
        <div className="calendar-grid" role="group" aria-label={monthLabel}>
          {days.map((day) => {
            const dateKey = toDateKey(day);
            const outsideMonth = day.getMonth() !== visibleMonth.getMonth();
            const isToday = dateKey === toDateKey(today);
            const itemCount = items.filter((item) => item.date === dateKey).length;
            return (
              <button
                type="button"
                className="calendar-day"
                data-outside-month={outsideMonth}
                data-today={isToday}
                aria-label={new Intl.DateTimeFormat('en', longDateFormat).format(day)}
                aria-pressed={selectedDate === dateKey}
                onClick={() => setSelectedDate(dateKey)}
                key={dateKey}
              >
                <span>{day.getDate()}</span>
                {itemCount > 0 && <small aria-label={`${itemCount} planned items`}>{itemCount}</small>}
              </button>
            );
          })}
        </div>
      </section>

      <div className="calendar-side">
        <img className="calendar-witch" src="/assets/calendar-witch-v0824.png" alt="" width="1254" height="1254" />
        <aside className="selected-day-card" aria-labelledby="selected-day-title">
          <p className="eyebrow">Selected day</p>
          <h2 id="selected-day-title">{selectedLabel}</h2>
          {selectedItems.length === 0 ? (
            <EmptyState
              description="Tasks and events for this date will appear together here."
              icon={CalendarX2}
              title="No plans for this day"
            />
          ) : (
            <ul className="calendar-item-list">
              {selectedItems.map((item) => <li key={`${item.kind}-${item.id}`}><span>{item.kind}{item.time ? ` at ${formatTime(item.time, timeFormat)}` : ''}</span><strong>{item.title}</strong></li>)}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
};
